import { useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';
import type { InteractionMessageResponse } from '@vagusx/clients';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { useLiveTranscript, type ConnectionState } from '@/hooks/use-live-transcript';

interface LiveCallTranscriptProps {
  interactionId: string;
  /** Whether the call is still on the line — gates the SSE subscription. */
  isLive: boolean;
}

function connectionNotice(state: ConnectionState, isLive: boolean): string | null {
  if (!isLive) return 'This call has ended.';
  if (state === 'error') return 'Reconnecting to the live transcript…';
  if (state === 'connecting') return 'Connecting to the live transcript…';
  // `disconnected` is the GRACEFUL close: the server emitted its terminal
  // `end` event and the hook latched, so it will never reconnect. Falling
  // through to `null` here left the green "Listening…" pulsing on a stream
  // that is shut for good — telling an operator a dead call is live until
  // the next roster poll drops the row.
  if (state === 'disconnected') return 'The live transcript has closed.';
  return null;
}

/**
 * The call as it happens, over the existing per-interaction SSE stream.
 *
 * Tool calls are shown, deliberately. They are the evidence the stage
 * reading in the rail is built from, so someone who distrusts "Confirming"
 * can see the `confirm_slots` that produced it. Only requests are
 * rendered — a response pairs with its request and doubles the row count
 * for no extra information at a glance.
 */
export function LiveCallTranscript({
  interactionId,
  isLive,
}: LiveCallTranscriptProps): React.ReactElement {
  const { messages, isLoading, connectionState } = useLiveTranscript(interactionId, isLive);
  const endRef = useRef<HTMLDivElement>(null);

  // Follow the conversation. `messages.length` rather than the array
  // identity: the hook rebuilds the merged array on every render, so
  // depending on the array itself would scroll on renders that added
  // nothing.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, interactionId]);

  const notice = connectionNotice(connectionState, isLive);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-5">
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-12 w-1/2 self-end" />
        <Skeleton className="h-12 w-3/5" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-5">
      {messages.length === 0 ? (
        <p className="text-center text-xs text-muted-foreground">Nothing said yet on this call.</p>
      ) : null}

      {messages.map((message) => (
        <TranscriptEntry key={message.id} message={message} />
      ))}

      {notice ? <p className="text-center text-xs text-muted-foreground">{notice}</p> : null}

      {isLive && !notice ? (
        <p className="flex items-center gap-2 text-xs italic text-muted-foreground">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" />
          Listening…
        </p>
      ) : null}

      <div ref={endRef} />
    </div>
  );
}

function TranscriptEntry({
  message,
}: {
  message: InteractionMessageResponse;
}): React.ReactElement | null {
  const at = new Date(message.timestamp).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });

  if (message.kind === 'agent_message') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%] rounded-xl rounded-br-sm bg-primary/10 px-3 py-2">
          <p className="text-[13.5px] leading-snug">{message.text}</p>
          <p className="mt-1 font-mono text-[10.5px] text-muted-foreground">{at}</p>
        </div>
      </div>
    );
  }

  if (message.kind === 'user_message') {
    return (
      <div className="flex">
        <div className="max-w-[78%] rounded-xl rounded-bl-sm bg-muted px-3 py-2">
          <p className="text-[13.5px] leading-snug">{message.text}</p>
          <p className="mt-1 font-mono text-[10.5px] text-muted-foreground">{at}</p>
        </div>
      </div>
    );
  }

  if (message.kind === 'tool_request') {
    return (
      <div className="flex justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-0.5 font-mono text-[11px] text-muted-foreground">
          {message.toolName}
        </span>
      </div>
    );
  }

  if (message.kind === 'tool_response') {
    const failed = message.toolStatus === 'error';
    // Only failures earn a row of their own. A successful response says
    // nothing its request did not already say, and a transcript where
    // every line is doubled is harder to read during a live call.
    if (!failed) return null;

    return (
      <div className="flex justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/5 px-2.5 py-0.5 font-mono text-[11px] text-destructive">
          <X className="h-3 w-3" />
          {message.toolName ?? 'tool'} failed
        </span>
      </div>
    );
  }

  if (message.kind === 'handoff') {
    return (
      <div className="flex justify-center">
        <span className="text-[11px] text-muted-foreground">
          Handed to {message.payload.toAgentId}
        </span>
      </div>
    );
  }

  if (message.kind === 'call_ended') {
    return (
      <div className="flex justify-center">
        <span className={cn('inline-flex items-center gap-1.5 text-[11px] text-muted-foreground')}>
          <Check className="h-3 w-3" />
          Call ended — {message.payload.reason.replace(/_/g, ' ')}
        </span>
      </div>
    );
  }

  return null;
}
